//syntax: https://openlayers.org/en/latest/examples/webgl-points-layer.html

import { VesselsTrackingModel } from "../models/vessels-tracking-model";
import { VoyFleetAlarmModel } from "../models/voy-fleet-alarm-model";
import { VesselCategories } from "../services/types/position-service.types";

export enum VesselIconColors {
    Alarm = "#B80012",
    Maersk = "#005487",
    NonMaersk = "#FFD029",
    Tug = "#E9B2B7",
    Tanker = "#BD3FE9",
    RoRo = "#4EB6AB",
    BulkCarrier = "#7BFC4F",
    Other = "#CFCFCF"
}

export type VesselIconFallbackColor = "#FFF"

// The order of array items determines which color is chosen for 
// the vessel e.g. alarm (red) is more important than maersk (blue),
// thus the reason for the strong type saftety
export type VesselColors = [
    [VesselCategories.Alarm, VesselIconColors.Alarm],
    [VesselCategories.Maersk, VesselIconColors.Maersk],
    [VesselCategories.NonMaersk, VesselIconColors.NonMaersk],
    [VesselCategories.Tug, VesselIconColors.Tug],
    [VesselCategories.Tanker, VesselIconColors.Tanker],
    [VesselCategories.RoRo, VesselIconColors.RoRo],
    [VesselCategories.BulkCarrier, VesselIconColors.BulkCarrier],
    [VesselCategories.Other, VesselIconColors.Other]
]

// The webGl syntax is recursive due to nested arrays,
// so the WebGlPointsSyntax can contain itself
type WebGlPointsSyntax = Array<string | number | WebGlPointsSyntax>;

export const vesselColors: VesselColors = [
    [VesselCategories.Alarm, VesselIconColors.Alarm,],
    [VesselCategories.Maersk, VesselIconColors.Maersk],
    [VesselCategories.NonMaersk, VesselIconColors.NonMaersk],
    [VesselCategories.Tug, VesselIconColors.Tug],
    [VesselCategories.Tanker, VesselIconColors.Tanker],
    [VesselCategories.RoRo, VesselIconColors.RoRo],
    [VesselCategories.BulkCarrier, VesselIconColors.BulkCarrier],
    [VesselCategories.Other, VesselIconColors.Other],
]

export const vesselColorsMap: Map<VesselCategories, VesselIconColors> = new Map(vesselColors);

export function generatePointsFillColors(): WebGlPointsSyntax {
    const webGLCode: WebGlPointsSyntax = []
    _addEntry(webGLCode, null, vesselColors, 0, vesselColors.length);
    return webGLCode;
}

function _addEntry(accumulatedEntries: WebGlPointsSyntax, latestEntry: WebGlPointsSyntax|null, vesselColors: VesselColors, currentIteration: number, length: number) {
    if (currentIteration+1 > length && latestEntry) {
        const fallbackColor: VesselIconFallbackColor = "#FFF";
        latestEntry.push(fallbackColor);
        return;
    }

    const newEntry = ['case', ['==', ['get', "vesselCategory"], vesselColors[currentIteration][0]], vesselColors[currentIteration][1]];

    if (latestEntry) {
        latestEntry.push(newEntry) 
    } else {
        accumulatedEntries.push(newEntry)
    }
    
    if (currentIteration < length) {
        _addEntry(accumulatedEntries, newEntry, vesselColors, currentIteration+1, length)
    }
}

export function getVoyageVesselCategory(imo: number): VesselCategories {
    const pos = VesselsTrackingModel!.allVesselPositions.get(imo)!;

    if (pos.isMaersk) {
        const alarms = VoyFleetAlarmModel!.getVesselAlarmsBasicInfo(imo);
        if (alarms.length) {
            return VesselCategories.Alarm;
        }
        return VesselCategories.Maersk;
    }

    if (pos.vesselCategory === VesselCategories.ContainerCargo && !pos.isMaersk) {
        return VesselCategories.NonMaersk;
    }

    return pos.vesselCategory
}

export function getVesselIconColor(imo: number) {
    const vesselCategory = getVoyageVesselCategory(imo);
    return vesselColorsMap.get(vesselCategory)!
}
